import type { CarDTO, LovCarDTO } from '@/features/api/extranet/dto/Demande/CAR'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import DocumentsContent from '@/features/interface/demande/car/steps/documents/DocumentsStep.vue'
import EmployeurContent from '@/features/interface/demande/car/steps/employeur/EmployeurStep.vue'
import IntroductionContent from '@/features/interface/demande/car/steps/introduction/IntroductionStep.vue'
import SyntheseContent from '@/features/interface/demande/car/steps/synthese/SyntheseStep.vue'
import type { SetupDemande } from '@/features/interface/demande/shared/components/demande-content/DemandeContent.types.ts'
import { storeToRefs } from 'pinia'
import { type Component, provide } from 'vue'
import { CAR_ACTIONS, useDemandeCARActions } from './useDemandeCARActions'
import { useDemandeCARStore } from './useDemandeCARStore'
import type { DemandeStore } from '@/features/interface/demande/shared'

type TabKey = 'Introduction' | 'Employeur' | 'Synthèse' | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  Introduction: IntroductionContent,
  Employeur: EmployeurContent,
  Synthèse: SyntheseContent,
  Documents: DocumentsContent,
} as const

const TABS_ERR_CONFIG: ErreurSummaryConfiguration<CarDTO> = {
  Introduction: { include: (state) => [state.validationNotice] },
  Employeur: {
    includeDeep: (state) => [state.employeur],
  },
  Synthèse: { include: (state) => [state.commentaire, state.acceptationCg, state.attestationSurHonneur] },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives],
  },
}

function customizeNatureFichiers(store: DemandeStore<'MODEL_CAR', LovCarDTO>) {
  const { lov } = storeToRefs(store)
  return lov.value.NatureFichier.map((item) => {
    if (item.code !== 'Convention') return item
    return {
      code: 'Convention',
      intitule: 'Convention ou devis',
    }
  })
}

export const SetupDemandeCar: SetupDemande<'MODEL_CAR', TabKey> = {
  useStore: useDemandeCARStore,
  injectActions: () => {
    const actions = useDemandeCARActions()
    provide(CAR_ACTIONS, actions)
    return actions
  },
  tabFirst: 'Introduction',
  tabsVue: TABS_VUES,
  tabsErrorConfig: (store) => {
    const { typeContextCourant, estTT } = storeToRefs(store)
    if (estTT.value && typeContextCourant.value === TypesContextesAutorisesEnum.Adherent)
      return TABS_ERR_CONFIG
    const { Financement: _, ...newTabs } = TABS_ERR_CONFIG
    return newTabs
  },
  customizeNatureFichiers,
} as const
